const emptyTeamStats = () => ({
  points: 0,
  fieldGoalsMade: 0,
  fieldGoalsAttempted: 0,
  threePointersMade: 0,
  threePointersAttempted: 0,
  freeThrowsMade: 0,
  freeThrowsAttempted: 0,
  rebounds: 0,
  assists: 0,
  steals: 0,
  blocks: 0,
  turnovers: 0,
  fouls: 0,
});

const emptyPlayerStats = (player, teamId) => ({
  id: player?.id || `${teamId}-${player?.jerseyNumber || 'x'}`,
  name: player?.userProfile?.fullName || player?.name || 'Unknown',
  jerseyNumber: player?.jerseyNumber ?? '—',
  teamId,
  points: 0,
  rebounds: 0,
  assists: 0,
  steals: 0,
  blocks: 0,
  fouls: 0,
  turnovers: 0,
  fieldGoalsMade: 0,
  fieldGoalsAttempted: 0,
  threePointersMade: 0,
  threePointersAttempted: 0,
  freeThrowsMade: 0,
  freeThrowsAttempted: 0,
});

const applyEventToTeam = (stats, eventType, points = 0) => {
  switch (eventType) {
    case 'twoPointMade':
    case 'fieldGoal':
      stats.points += points || 2;
      stats.fieldGoalsMade += 1;
      stats.fieldGoalsAttempted += 1;
      if ((points || 2) === 3) {
        stats.threePointersMade += 1;
        stats.threePointersAttempted += 1;
      }
      break;
    case 'twoPointMiss':
      stats.fieldGoalsAttempted += 1;
      break;
    case 'threePointMade':
      stats.points += points || 3;
      stats.fieldGoalsMade += 1;
      stats.fieldGoalsAttempted += 1;
      stats.threePointersMade += 1;
      stats.threePointersAttempted += 1;
      break;
    case 'threePointMiss':
      stats.fieldGoalsAttempted += 1;
      stats.threePointersAttempted += 1;
      break;
    case 'freeThrowMade':
    case 'freeThrow':
      stats.points += points || 1;
      stats.freeThrowsMade += 1;
      stats.freeThrowsAttempted += 1;
      break;
    case 'freeThrowMiss':
      stats.freeThrowsAttempted += 1;
      break;
    case 'rebound':
      stats.rebounds += 1;
      break;
    case 'assist':
      stats.assists += 1;
      break;
    case 'steal':
      stats.steals += 1;
      break;
    case 'block':
      stats.blocks += 1;
      break;
    case 'turnover':
      stats.turnovers += 1;
      break;
    case 'foul':
    case 'technicalFoul':
      stats.fouls += 1;
      break;
    default:
      if (points > 0) stats.points += points;
      break;
  }
};

/**
 * Aggregate match_events into team totals and player box scores.
 */
export const buildMatchReportFromEvents = ({
  events = [],
  homeTeamId,
  awayTeamId,
  homeScore = 0,
  awayScore = 0,
} = {}) => {
  const home = emptyTeamStats();
  const away = emptyTeamStats();
  const playersById = new Map();

  const scoringEvents = (events || []).filter(
    (e) => e?.eventType && e.eventType !== 'quarter_end' && e.eventType !== 'period'
  );

  scoringEvents.forEach((event) => {
    const side =
      event.teamId === homeTeamId ? home : event.teamId === awayTeamId ? away : null;
    if (!side) return;

    applyEventToTeam(side, event.eventType, event.points || 0);

    if (event.playerId || event.player) {
      const key = event.playerId || event.player?.id;
      if (!playersById.has(key)) {
        playersById.set(key, emptyPlayerStats(event.player, event.teamId));
      }
      applyEventToTeam(playersById.get(key), event.eventType, event.points || 0);
    }
  });

  const hasDetailedStats = scoringEvents.some((e) =>
    [
      'twoPointMade',
      'twoPointMiss',
      'threePointMade',
      'threePointMiss',
      'freeThrowMade',
      'freeThrowMiss',
      'fieldGoal',
      'freeThrow',
      'rebound',
      'assist',
      'steal',
      'block',
      'turnover',
      'foul',
    ].includes(e.eventType)
  );

  // Prefer recorded final score when event points don't cover the full game
  if (!hasDetailedStats || home.points === 0) home.points = homeScore || home.points;
  if (!hasDetailedStats || away.points === 0) away.points = awayScore || away.points;

  const players = Array.from(playersById.values());
  const homePlayers = players
    .filter((p) => p.teamId === homeTeamId)
    .sort((a, b) => (b.points || 0) - (a.points || 0));
  const awayPlayers = players
    .filter((p) => p.teamId === awayTeamId)
    .sort((a, b) => (b.points || 0) - (a.points || 0));

  return {
    home: { ...home, totalRebounds: home.rebounds },
    away: { ...away, totalRebounds: away.rebounds },
    homePlayers,
    awayPlayers,
    hasDetailedStats,
    playByPlay: [...(events || [])].sort((a, b) => {
      const qa = a.quarter ?? 0;
      const qb = b.quarter ?? 0;
      if (qa !== qb) return qa - qb;
      return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
    }),
  };
};
